import { useEffect, useRef, useState } from "react";
import { useLanguage } from "../../i18n/LanguageContext";
import { demoScript, DEMO_DURATION } from "../../data/demoScript";
import { Reveal } from "../Reveal";
import { Play, Pause, Bot, User, Headphones, Volume2, VolumeX } from "lucide-react";

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

const AudioDemo = () => {
  const { t } = useLanguage();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(DEMO_DURATION);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);

  const toggle = () => {
    if (audioRef.current) {
      if (playing) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(() => {});
      }
    }
  };

  const seek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const next = ratio * duration;
    setTime(next);
    if (audioRef.current) audioRef.current.currentTime = next;
  };

  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (audioRef.current) {
      audioRef.current.volume = val;
      audioRef.current.muted = false;
      setMuted(false);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !muted;
      setMuted(!muted);
    }
  };

  const progress = duration ? (time / duration) * 100 : 0;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setTime(audio.currentTime);
    const onLoadedMetadata = () => {
      setDuration(audio.duration || DEMO_DURATION);
      audio.volume = volume;
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => { setPlaying(false); setTime(0); };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  return (
    <section id="demo" data-testid="demo-section" className="relative mx-auto max-w-7xl scroll-mt-28 px-6 py-24 lg:px-10 lg:py-32">
      <div className="pointer-events-none absolute start-0 top-1/3 h-96 w-96 rounded-full bg-neon-muted blur-[140px]" />
      <Reveal>
        <p className="overline-tag" data-testid="demo-overline"><Headphones size={12} className="me-2 inline" />{t.demo.overline}</p>
        <h2 className="mt-4 font-display text-3xl font-extrabold md:text-5xl">{t.demo.title}</h2>
        <p className="mt-4 max-w-xl text-sm text-zinc-400 md:text-base">{t.demo.sub}</p>
      </Reveal>

      <div className="mt-12 grid gap-8 lg:grid-cols-12">
        <Reveal delay={0.1} className="lg:col-span-5">
          <div className="glass-panel neon-glow rounded-3xl p-8" data-testid="audio-player">
            <audio
              ref={audioRef}
              src="/assets/demo-salon.mp3"
              preload="metadata"
              volume={volume}
            />

            <div className="flex items-end justify-center gap-[3px]" aria-hidden="true">
              {Array.from({ length: 32 }).map((_, i) => (
                <span key={i} className={playing ? "eq-bar w-1 rounded-full bg-neon/70" : "w-1 rounded-full bg-zinc-700"}
                  style={{ height: `${10 + Math.abs(Math.sin(i * 1.7)) * 34}px`, animationDelay: `${(i % 8) * 0.09}s` }} />
              ))}
            </div>

            <div className="mt-8 flex items-center gap-5">
              <button onClick={toggle} data-testid="audio-play-toggle"
                className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-gold text-void transition-[transform,box-shadow] duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(212,175,55,0.4)]">
                {playing ? <Pause size={24} /> : <Play size={24} className="ms-0.5" />}
              </button>
              <div className="flex-1">
                <div onClick={seek} data-testid="audio-progress" className="group h-2 cursor-pointer rounded-full bg-white/10">
                  <div className="h-full rounded-full bg-gradient-to-r from-gold to-neon transition-[width] duration-150" style={{ width: `${progress}%` }} />
                </div>
                <div className="mt-2 flex justify-between text-xs text-zinc-500">
                  <span data-testid="audio-time-current">{fmt(time)}</span>
                  <span data-testid="audio-time-total">{fmt(duration)}</span>
                </div>
              </div>
            </div>

            {/* Volume Controls */}
            <div className="mt-6 flex items-center gap-3">
              <button onClick={toggleMute} className="text-zinc-400 hover:text-white transition-colors" aria-label="Toggle mute">
                {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={muted ? 0 : volume}
                onChange={handleVolumeChange}
                className="w-24 h-1 bg-zinc-700 rounded-full appearance-none cursor-pointer accent-gold"
                aria-label="Volume"
              />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2} className="lg:col-span-7">
          <div className="glass-panel rounded-3xl p-6 md:p-8" data-testid="captions-box">
            <div className="flex max-h-[26rem] flex-col gap-2 overflow-y-auto pe-2">
              {demoScript.map((line, i) => {
                const active = time >= line.start && time < line.end;
                return (
                  <div key={i} data-testid={`caption-${i + 1}`} className={`caption-line ${active ? "active" : ""}`}>
                    <span className={`font-display text-xs font-bold uppercase tracking-widest ${line.speaker === "agent" ? "speaker-agent" : "text-zinc-500"}`}>
                      {line.speaker === "agent" ? <Bot size={12} className="me-1 inline" /> : <User size={12} className="me-1 inline" />}
                      {line.speaker === "agent" ? t.demo.agent : t.demo.customer}
                    </span>
                    <p className="caption-text mt-1 text-sm leading-relaxed md:text-base" dir={line.lang === "ar" ? "rtl" : "ltr"}>
                      {line.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.25}>
        <p className="mt-8 text-xs text-zinc-600" data-testid="demo-hint">{t.demo.hint}</p>
      </Reveal>
    </section>
  );
};

export default AudioDemo;
